import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface ToastItem {
  id: number;
  text: string;
  points?: number;
}

interface ToastCtxValue {
  show: (text: string, points?: number) => void;
  burst: (originEl: HTMLElement | null, points: number) => void;
}

const ToastCtx = createContext<ToastCtxValue | undefined>(undefined);

let _seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timeouts = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const tid = timeouts.current.get(id);
    if (tid) clearTimeout(tid);
    timeouts.current.delete(id);
  }, []);

  const show = useCallback(
    (text: string, points?: number) => {
      const id = ++_seq;
      setItems((prev) => [...prev, { id, text, points }]);
      const tid = setTimeout(() => remove(id), 2800);
      timeouts.current.set(id, tid);
    },
    [remove],
  );

  const burst = useCallback((originEl: HTMLElement | null, points: number) => {
    if (!originEl || typeof document === 'undefined') return;
    const r = originEl.getBoundingClientRect();
    const el = document.createElement('div');
    el.className = 'burst';
    el.textContent = `+${points}`;
    el.style.left = `${r.left + r.width / 2}px`;
    el.style.top = `${r.top - 8}px`;
    el.style.transform = 'translate(-50%, 0)';
    document.body.appendChild(el);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(12);
      } catch {
        /* noop */
      }
    }
    setTimeout(() => el.remove(), 1000);
  }, []);

  useEffect(() => {
    const map = timeouts.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  const value = useMemo<ToastCtxValue>(() => ({ show, burst }), [show, burst]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toast-host" aria-live="polite" aria-atomic="true">
        {items.map((t) => (
          <div key={t.id} className="toast" role="status">
            <span>{t.text}</span>
            {typeof t.points === 'number' && (
              <span className="plus-pts" aria-label={`+${t.points} points`}>
                +{t.points}
              </span>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastCtxValue {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
